import { useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import API from "services/axios";
import { PICKUP_ADDRESS, isPickupMethod } from "./pickupAddress";

/**
 * Lets the CUSTOMER correct their addresses from the approval link, before
 * they approve the card. Shipping first, then billing — shipping is the one
 * that decides where the parcel goes, so it leads.
 *
 * No auth header anywhere in here on purpose: the customer has no account.
 * The approval token identifies the order server-side, and the endpoints
 * resolve the work order from it rather than trusting anything sent from the
 * page — see controllers/customerAddressEdit.controller.js.
 *
 * Validate is OFFERED, not required. UPS cannot resolve every deliverable
 * address (rural routes, new builds, military addresses), and refusing to save
 * one it does not recognise would leave a customer unable to fix an address
 * that is perfectly correct. What UPS said is recorded with the change
 * instead, so staff can see whether anyone confirmed it.
 *
 * Each address is saved as its OWN request. They are independent corrections,
 * and one failing must not silently discard the other — the API also writes a
 * separate comment per address, which is what the staff cards read.
 */

const FIELDS = [
  { key: "addressCompany", label: "Company", required: false, wide: false },
  { key: "attentionTo", label: "Attention to", required: false, wide: false },
  { key: "addressLine1", label: "Address line 1", required: true, wide: true },
  { key: "addressLine2", label: "Address line 2", required: false, wide: true },
  { key: "city", label: "City", required: true, wide: false },
  { key: "state", label: "State", required: true, wide: false },
  { key: "zip", label: "ZIP", required: true, wide: false },
  { key: "country", label: "Country", required: false, wide: false },
];

const seedFrom = (a) => {
  const seed = {};
  FIELDS.forEach(({ key }) => {
    seed[key] = String(a?.[key] || "");
  });
  if (!seed.country) seed.country = "US";
  return seed;
};

const asLine = (a) => {
  if (!a) return "—";
  const cityLine = [a.city, a.state, a.zip].filter(Boolean).join(" ");
  return (
    [a.addressLine1, a.addressLine2, cityLine].filter(Boolean).join(", ") || "—"
  );
};

const sameAddress = (a, b) =>
  FIELDS.every(
    (f) =>
      String(a?.[f.key] || "").trim().toUpperCase() ===
      String(b?.[f.key] || "").trim().toUpperCase(),
  );

/** One address block: its fields, its Validate, its UPS result. */
function AddressSection({ title, hint, form, setForm, checked, setChecked, onValidate, checking, disabled }) {
  const idPrefix = String(title || "address")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");

  const set = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    // Any edit invalidates a check made against the previous text, or the
    // record could claim UPS confirmed an address it never saw.
    setChecked(null);
  };

  const missing = FIELDS.filter(
    (f) => f.required && !String(form[f.key] || "").trim(),
  ).map((f) => f.label);

  const useSuggestion = () => {
    const s = checked?.suggested;
    if (!s) return;
    setForm((p) => ({
      ...p,
      addressLine1: s.addressLine1 || "",
      addressLine2: s.addressLine2 || "",
      city: s.city || "",
      state: s.state || "",
      zip: s.zip || "",
      country: s.country || p.country,
    }));
    // Still confirmed: the form now holds exactly what UPS returned.
    setChecked({ ...checked, applied: true });
  };

  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <h5 className="mb-0">{title}</h5>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={onValidate}
          disabled={checking || disabled || missing.length > 0}
          title={
            missing.length
              ? "Fill in the required fields first"
              : "Check this address with UPS"
          }
        >
          {checking ? "Checking…" : "Validate"}
        </button>
      </div>
      <div className="text-muted mb-3" style={{ fontSize: 13 }}>
        {hint}
      </div>

      <div className="row g-2">
        {FIELDS.map((f) => {
          // The labels sat beside their inputs without being attached to
          // them: clicking the text did nothing, and a screen reader read an
          // unlabelled box. Prefixed by section so a second address on the
          // same screen cannot collide.
          const id = `${idPrefix}-${f.key}`;
          return (
            <div className={f.wide ? "col-12" : "col-12 col-md-6"} key={f.key}>
              <label
                htmlFor={id}
                className="form-label mb-1"
                style={{ fontSize: 14 }}
              >
                {f.label}
                {f.required && <span className="text-danger"> *</span>}
              </label>
              <input
                id={id}
                type="text"
                className="form-control"
                value={form[f.key]}
                disabled={disabled}
                onChange={(e) => set(f.key, e.target.value)}
              />
            </div>
          );
        })}
      </div>

      {checked && (
        <div
          className={`alert py-2 px-3 mt-3 mb-0 ${
            checked.ok ? "alert-success" : "alert-warning"
          }`}
        >
          {checked.ok ? (
            <>
              <div className="fw-semibold mb-1">UPS recognised this address</div>
              <div style={{ fontSize: 14 }}>{asLine(checked.suggested)}</div>
              {!checked.applied && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success mt-2"
                  onClick={useSuggestion}
                >
                  Use UPS&apos;s version
                </button>
              )}
            </>
          ) : (
            <>
              <div className="fw-semibold mb-1">
                UPS could not confirm this address
              </div>
              <div style={{ fontSize: 14 }}>
                {checked.reason} — you can still save it if you know it is
                right.
              </div>
            </>
          )}
        </div>
      )}

      {missing.length > 0 && (
        <div className="text-muted mt-2 mb-0" style={{ fontSize: 13 }}>
          Still needed: {missing.join(", ")}.
        </div>
      )}
    </div>
  );
}

export default function CustomerAddressEditModal({
  currentShipping,
  // The company's shipment methods to choose from, and the id currently on the
  // order. Billing is intentionally not editable here -- the customer corrects
  // where the parcel goes and how it ships, not where the invoice is sent.
  shipmentMethods = [],
  currentShipmentMethodId = null,
  token,
  editedBy,
  onHide,
  onSaved,
}) {
  const [shipping, setShipping] = useState(() => seedFrom(currentShipping));
  const [shipChecked, setShipChecked] = useState(null);
  const [checkingField, setCheckingField] = useState(null);
  const [methodId, setMethodId] = useState(
    currentShipmentMethodId ? String(currentShipmentMethodId) : "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // What the shipping address was before a pickup method replaced it, so
  // changing your mind puts it back. Without this, picking pickup and then
  // switching to Ground would ship the order to our own door.
  const beforePickup = useRef(null);

  /*   Pickup auto-fill
   *
   * Choosing a pickup method fills the shipping address with the address the
   * order is collected FROM. The card already printed that address as text
   * when the method said pickup, but the SAVED address stayed the customer's
   * -- so the screen said one thing and the label, the PDF and the shipment
   * said another. Filling the form here makes it real data.
   *
   * Only on a change of method, never when the modal opens: an order that is
   * already pickup may have an address staff set deliberately, and quietly
   * rewriting it on open would be an edit nobody asked for.
   ********************************************* */
  const handleMethodChange = (nextId) => {
    const nameOf = (id) =>
      shipmentMethods.find((m) => String(m.id) === String(id))?.shippingMethod;

    const wasPickup = isPickupMethod(nameOf(methodId));
    const nowPickup = isPickupMethod(nameOf(nextId));

    if (nowPickup && !wasPickup) {
      beforePickup.current = shipping;
      setShipping({ ...PICKUP_ADDRESS });
      setShipChecked(null); // a previous UPS check described a different address
    } else if (!nowPickup && wasPickup && beforePickup.current) {
      setShipping(beforePickup.current);
      beforePickup.current = null;
      setShipChecked(null);
    }

    setMethodId(nextId);
  };

  const validate = async () => {
    setCheckingField("shipping");
    setError(null);
    try {
      const { data } = await API.post(
        "/wo-lot-approval/validate-address-by-link",
        { woToken: token, address: shipping },
      );
      if (!data?.success) throw new Error(data?.message || "Could not check");
      setShipChecked(data.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setShipChecked(null);
    }
    setCheckingField(null);
  };

  const shippingChanged = !sameAddress(shipping, currentShipping);
  const methodChanged =
    String(methodId || "") !== String(currentShipmentMethodId || "");

  const missing = FIELDS.filter(
    (f) => f.required && !String(shipping[f.key] || "").trim(),
  ).length;

  // Nothing to do unless something actually changed.
  const hasChange = shippingChanged || methodChanged;

  const save = async () => {
    if (!hasChange || (shippingChanged && missing)) return;
    setSaving(true);
    setError(null);
    try {
      // Address first, then method. Sequential so a failure on the second
      // leaves the first already written rather than both half-done.
      if (shippingChanged) {
        const { data } = await API.post(
          "/wo-lot-approval/update-address-by-link",
          {
            woToken: token,
            editedBy,
            field: "shipping",
            address: shipping,
            validated: shipChecked ? !!shipChecked.ok : null,
          },
        );
        if (!data?.success) throw new Error(data?.message || "Could not save");
      }
      if (methodChanged) {
        const { data } = await API.post(
          "/wo-lot-approval/update-shipment-method-by-link",
          { woToken: token, editedBy, shipmentMethodId: Number(methodId) },
        );
        if (!data?.success) throw new Error(data?.message || "Could not save");
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={saving ? undefined : onHide} size="lg" centered scrollable>
      <Modal.Header closeButton={!saving}>
        <Modal.Title className="h5 mb-0">Edit shipping</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <div className="alert alert-danger py-2 px-3">{error}</div>}

        <div className="text-muted mb-3" style={{ fontSize: 14 }}>
          Correct where this order ships and how. Only what you change is saved,
          and the address it replaces is kept on the order so we can see what it
          was.
        </div>

        {/* Shipment method first: the same list staff pick from, so the
            customer can correct how the order ships without a call. */}
        <div className="border rounded p-3 mb-3">
          <h5 className="mb-1">Shipment method</h5>
          <div className="text-muted mb-3" style={{ fontSize: 13 }}>
            How the order is shipped.
          </div>
          {shipmentMethods.length ? (
            <select
              className="form-select"
              value={methodId}
              disabled={saving}
              onChange={(e) => handleMethodChange(e.target.value)}
            >
              {/* No blank option: an order always ships somehow, and offering
                  "none" would let a save clear it. */}
              {shipmentMethods.map((m) => (
                <option key={m.id} value={String(m.id)}>
                  {m.shippingMethod}
                </option>
              ))}
            </select>
          ) : (
            <div className="text-muted" style={{ fontSize: 14 }}>
              No shipment methods are available to choose from.
            </div>
          )}
        </div>

        <AddressSection
          title="Shipping address"
          hint={
            // Said out loud, because the fields change under the customer the
            // moment they choose pickup. An unexplained rewrite of an address
            // they just typed reads as a bug.
            isPickupMethod(
              shipmentMethods.find((m) => String(m.id) === String(methodId))
                ?.shippingMethod,
            )
              ? "You chose pickup, so this is where the order is collected from."
              : "Where the order is delivered."
          }
          form={shipping}
          setForm={setShipping}
          checked={shipChecked}
          setChecked={setShipChecked}
          onValidate={validate}
          checking={checkingField === "shipping"}
          disabled={saving}
        />
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onHide}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={saving || !hasChange || (shippingChanged && missing > 0)}
          title={
            !hasChange
              ? "Nothing has been changed yet"
              : shippingChanged && missing
                ? "Fill in the required fields first"
                : undefined
          }
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
